import { useState, useMemo } from 'react'
import { useFirestoreLoaded } from '../../firebase/useFirestore'
import useLocationFilter from '../../hooks/useLocationFilter'
import useDashboardCardSetting from '../../hooks/useDashboardCardSetting'
import { DASHBOARD_FILTER_FIELDS } from '../../helpers/dashboardHelpers/dashboardCardFilters'
import filterPersonasByStatus from '../../helpers/personas/filterPersonasByStatus'
import presenceAttendanceStats from '../../helpers/personas/presenceAttendanceStats'
import { usePersonaPresenceMonth } from '../../hooks/usePersonaPresenceReports'
import usePersonaPresenceYear from '../../hooks/usePersonaPresenceYear'
import PersonasPresenceVisualization from '../dataVisualizations/PersonasPresenceVisualization'
import DashboardFilterBar from './DashboardFilterBar'
import DashboardLocationFilter from './DashboardLocationFilter'
import DashboardPersonaStatusFilter from './DashboardPersonaStatusFilter'
import DashboardCardEmptyState from './DashboardCardEmptyState'
import { FILTER_EMPTY_STATE } from './dashboardEmptyStateConfig'
import ExportBar from '../common/ExportBar'
import buildPresenceExportRows, { PRESENCE_EXPORT_HEADER } from '../../helpers/export/buildPresenceExportRows'
import downloadDatedExport from '../../helpers/export/downloadDatedExport'
import filterExportRows from '../../helpers/export/filterExportRows'
import fetchPresenceReportsInRange from '../../helpers/export/fetchPresenceReportsInRange'
import { useWithPersonaLocations } from '../../hooks/usePersonaLocations'

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Dashboard wrapper around PersonasPresenceVisualization. Owns the
// month/year state and the filters; de query's staan in
// `usePersonaPresenceReports` (één maand) en `usePersonaPresenceYear` (een
// heel jaar), zodat de live-report-widget de maandvariant kan hergebruiken.
//
// De kaart haalt álle deelnemers op en filtert daarna op status. Dat was
// eerder een `where('Status','==','active')` in de query, en daardoor was er
// geen enkele weg naar de aanwezigheid van iemand die inmiddels uitgestroomd
// is — terwijl juist die mensen in een jaarverantwoording horen.

const DashboardPersonasCard = ({ item }) => {
    // `loaded` erbij: zolang de deelnemers onderweg zijn is elke lijst leeg, en
    // een lege lijst laat de kaart anders beweren dat er niets geregistreerd is.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const [allPersonas, personasLoaded] = useFirestoreLoaded('Personas')
    const [selectedStatus, setSelectedStatus] = useDashboardCardSetting(
        item,
        DASHBOARD_FILTER_FIELDS.SelectedPersonaStatus,
        '',
    )

    // Verrijkt met de locaties uit de roosterregels, net als elke andere
    // deelnemerkaart — anders filtert deze op het oude veld.
    const personasMetLocaties = useWithPersonaLocations(allPersonas)
    const personas = useMemo(
        () => filterPersonasByStatus(personasMetLocaties, selectedStatus),
        [personasMetLocaties, selectedStatus],
    )

    const { filteredItems, selectedLocationId, setSelectedLocationId } =
        useLocationFilter(personas, { item })
    const [exportMessage, setExportMessage] = useState('')
    const [month, setMonth] = useState(new Date().getMonth() + 1)
    const [year, setYear] = useState(new Date().getFullYear())
    const [view, setView] = useState('month')

    const month_ = usePersonaPresenceMonth(month, year, { enabled: view === 'month' })
    const year_ = usePersonaPresenceYear(year, { enabled: view === 'year' })

    const { reports, loaded: reportsLoaded, error } = view === 'year' ? year_ : month_

    // Alles is pas bekend als béíde reads binnen zijn: de rapporten en de
    // deelnemers waar ze bij horen.
    const loaded = reportsLoaded && personasLoaded

    // De rapporten van deelnemers die er nu niet bij staan tellen niet mee —
    // anders slaat het percentage op meer mensen dan de tabel toont. Dit
    // rekenwerk deelt de kaart met de live-report-widget.
    const { reports: filteredReports, percentage: attendancePercentage } = useMemo(
        () => presenceAttendanceStats(reports, filteredItems),
        [reports, filteredItems],
    )

    // Vaste identiteit voor de standaardperiode van de export. `ExportModal`
    // zet zijn datumvelden terug zodra deze twee van waarde veranderen, en een
    // inline `new Date(...)` is bij élke render een nieuw object — dan wist een
    // willekeurige snapshot (een collega die een deelnemer aanpast) de datums
    // die de gebruiker net had ingetypt, terwijl het venster openstaat.
    // AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
    const exportPeriod = useMemo(() => (
        view === 'year'
            ? { start: new Date(year, 0, 1), end: new Date(year, 11, 31) }
            : { start: new Date(year, month - 1, 1), end: new Date(year, month, 0) }
    ), [view, year, month])

    // De kaart toont één periode, maar een export mag over een vrije periode
    // gaan; daarom wordt daarvoor apart opgehaald op het moment van klikken.
    const exportHandler = async (selection) => {
        const reports = await fetchPresenceReportsInRange(selection.startDate, selection.endDate)

        // 'Alles' betekent alle deelnemers die de kaart toont, niet alles wat er
        // in die periode in de database staat: anders verschijnen er regels van
        // deelnemers die hier niet in de lijst staan.
        const shown = filteredItems.map((persona) => persona.ID)
        const ids = selection.scope === 'selected' ? selection.selectedIds : shown

        const rows = filterExportRows(reports, { ...selection, scope: 'selected', selectedIds: ids }, {
            getId: (report) => report.PersonaID,
            getDate: (report) => (report?.Timestamp?.toDate ? report.Timestamp.toDate() : null),
        })

        const written = downloadDatedExport(buildPresenceExportRows(rows, filteredItems), {
            prefix: 'Aanwezigheid',
            format: selection.format,
            header: PRESENCE_EXPORT_HEADER,
        })

        if (!written) setExportMessage('Geen aanwezigheid gevonden voor deze keuze.')
        else setExportMessage('')
    }

    // 'Nog geen aanwezigheid geregistreerd' is een bewering aan de lezer, en die
    // is onwaar zolang er nog gehaald wordt of het ophalen mislukt is. De takken
    // staan daarom op zekerheid: eerst 'weet ik niet', dan pas 'leeg'.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const isEmpty = view === 'month' && loaded && !error && filteredReports.length === 0

    // Ook het statusfilter kan de reden zijn dat er niets staat; zonder deze
    // helft krijgt 'Alleen uitgestroomd' zonder registraties de absolute
    // melding in plaats van de filtermelding mét wisknop.
    const hasActiveFilter = !!selectedLocationId || !!selectedStatus
    const isFilterEmpty = isEmpty && hasActiveFilter && reports.length > 0

    const activeFilterCount = (selectedLocationId ? 1 : 0) + (selectedStatus ? 1 : 0)

    const clearFilters = () => {
        setSelectedLocationId('')
        setSelectedStatus('')
    }

    return (
        <div className='dashboard-personas-card'>
            <p className='new-dashboard-card-label'>
                <span>Aanwezigheid</span>
                <span className='new-dashboard-card-label-type new-dashboard-card-label-type-personas'>
                    Medewerkers
                </span>
            </p>
            <ExportBar
                pageHeader='Aanwezigheid exporteren'
                itemsLabel='Deelnemers'
                items={filteredItems.map((persona) => ({ id: persona.ID, label: persona.Name }))}
                onExport={exportHandler}
                defaultStartDate={exportPeriod.start}
                defaultEndDate={exportPeriod.end}
                emptyMessage='Geen deelnemers gevonden.'
            />
            {exportMessage && <p className='dashboard-export-message' role='alert'>{exportMessage}</p>}
            <DashboardFilterBar
                onClear={clearFilters}
                activeFilterCount={activeFilterCount}
                hasFilters
            >
                <DashboardPersonaStatusFilter
                    selectedStatus={selectedStatus}
                    onChange={setSelectedStatus}
                />
                {/* De locatiechip haalt `Locations` zelf op en geeft null terug
                    zolang er niets te kiezen valt; een tweede abonnement hier
                    zou de chip tijdens het laden alleen maar laten flikkeren. */}
                <DashboardLocationFilter
                    selectedLocationId={selectedLocationId}
                    onChange={setSelectedLocationId}
                />
            </DashboardFilterBar>
            <PersonasPresenceVisualization
                attendancePercentage={attendancePercentage}
                personas={filteredItems}
                month={month}
                year={year}
                presenceReports={filteredReports}
                onMonthChange={setMonth}
                onYearChange={setYear}
                view={view}
                onViewChange={setView}
                yearReports={filteredReports}
                yearLoaded={loaded}
                error={error}
                emptyState={isFilterEmpty ? (
                    <DashboardCardEmptyState
                        {...FILTER_EMPTY_STATE}
                        onCtaClick={clearFilters}
                    />
                ) : isEmpty ? (
                    <DashboardCardEmptyState type='personas-presence' />
                ) : null}
            />
        </div>
    )
}

export default DashboardPersonasCard
